/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {optionsHelper} from '../helpers/options.helper';
import {httpOptions} from '../options/http.options';

/**
 * The `http` service provide ability that facilitates communication with the remote HTTP servers.
 *
 * Setting HTTP Headers
 * --------------------
 * The `http` service will automatically add certain HTTP headers to all requests.
 * These defaults can be fully configured by accessing the @see {http.options}, which currently
 * contains this default configuration:
 *
 * ```yaml
 *    # Configurations that are common for all requests
 *    common:
 *        cache: default
 *        credentials: same-origin
 *        redirect: follow
 *        headers:
 *          accept: 'application/json, text/html, text/plain'
 *          x-requested-with: XMLHttpRequest
 *
 *    # Configurations applied for `POST` method
 *    post:
 *        body: {}
 *        headers:
 *          content-type: application/json
 *
 *    # Configurations applied for `PUT` method
 *    put:
 *        body: {}
 *        headers:
 *          content-type: application/json
 *
 *    # Configurations applied for `PATCH` method
 *    patch:
 *        body: {}
 *        headers:
 *          content-type: application/json
 * ```
 *
 * To add or overwrite these defaults, simply create your own options `http.options` objects.
 * To add headers for an HTTP method other than `POST` or `PUT`, simply add a new object with the lowercased HTTP method
 * name as the key. Eg: `get: { headers: {'Header-Name': 'value'}}`.
 *
 * You also add or overwrite these values using the `configs` param for individual request.
 *
 *
 * Shortcut Methods
 * ----------------
 * All shortcut methods require passing in the URL, and request data must be passed in for POST/PUT/PATCH requests.
 * An optional configs can be passed as the last argument.
 *
 * @see HttpService~get    (url, configs = {})
 * @see HttpService~delete (url, configs = {})
 * @see HttpService~head   (url, configs = {})
 * @see HttpService~jsonp  (url, configs = {})
 * @see HttpService~post   (url, data, configs = {})
 * @see HttpService~put    (url, data, configs = {})
 * @see HttpService~patch  (url, data, configs = {})
 */
export const httpService = {
  /**
   * @param {string}      url     Absolute or relative URL of the resource that is being requested.
   * @param {RequestInit} configs An object containing any custom settings that you want to apply to the request.
   *
   * @return {Promise<Response>} A `Promise` that resolves to the `Response`
   */
  async send(url, configs = {}) {
    const method = (configs.method || 'GET').toLowerCase();

    configs = optionsHelper.getOptions(httpOptions, ['default', method, configs]);

    return fetch(url, configs);
  },

  /**
   * Shortcut method to perform `GET` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {RequestInit} configs Configuration object
   *
   * @return {Promise<Response>} A `Promise` that resolves to the `Response`
   */
  async get(url, configs = {}) {
    return this.send(url, {...configs, method: 'get'});
  },

  /**
   * Shortcut method to perform `DELETE` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {RequestInit} configs Configuration object
   *
   * @return {Promise<Response>} A `Promise` that resolves to the `Response`
   */
  async delete(url, configs = {}) {
    return this.send(url, {...configs, method: 'delete'});
  },

  /**
   * Shortcut method to perform `HEAD` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {RequestInit} configs Configuration object
   *
   * @return {Promise<Response>} A `Promise` that resolves to the `Response`
   */
  async head(url, configs = {}) {
    return this.send(url, {...configs, method: 'head'});
  },

  /**
   * Shortcut method to perform `JSONP` request.
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request.
   *                              The name of the callback should be the string `JSON_CALLBACK`
   * @param {any}         data    Request content
   * @param {RequestInit} configs Configuration object
   *
   * @return {Promise<Response>} A `Promise` that resolves to the `Response`
   */
  async jsonp(url, configs = {}) {
    return this.send(url, {...configs, method: 'jsonp'});
  },

  /**
   * Shortcut method to perform `POST` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {any}         data    Request content
   * @param {RequestInit} configs Configuration object
   *
   * @return {Promise<Response>} A `Promise` that resolves to the `Response`
   */
  async post(url, data, configs = {}) {
    return this.send(url, {...configs, body: data, method: 'POST'});
  },

  /**
   * Shortcut method to perform `PUT` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {any}         data    Request content
   * @param {RequestInit} configs Configuration object
   *
   * @return {Promise<Response>} A `Promise` that resolves to the `Response`
   */
  async put(url, data, configs = {}) {
    return this.send(url, {...configs, body: data, method: 'put'});
  },

  /**
   * Shortcut method to perform `PATCH` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {any}         data    Request content
   * @param {RequestInit} configs Configuration object
   *
   * @return {Promise<Response>} A `Promise` that resolves to the `Response`
   */
  async patch(url, data, configs = {}) {
    return this.send(url, {...configs, body: data, method: 'patch'});
  },
};
